import UserLocationForm from 'components/modules/UserLocationForm';
import Button from 'components/primitives/Button';
import SelectInput from 'components/primitives/SelectInput';
import SelectOption from 'components/primitives/SelectOption';
import TextInput from 'components/primitives/TextInput';
import { FC } from 'react';
import { isDev } from 'utils/environment';

type CountryCode = {
  title: string;
  code: string;
};

export interface AddressFormProps {
  onBack: () => void;
  isLoading?: boolean;
  isGoogleSignupCompleted?: boolean;
  setFieldValue: (field: string, value: any) => void;
  countryCodes: CountryCode[];
}

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

const AddressForm: FC<AddressFormProps> = ({
  onBack,
  isLoading = false,
  isGoogleSignupCompleted = false,
  setFieldValue,
  countryCodes,
}) => {
  const getTitleFromCode = (code: string) => {
    return countryCodes.find((country) => country.code === code)?.title;
  };
  return (
    <div className="flex flex-col justify-center">
      <h3 className="text-lg font-semibold mb-4 text-center">
        Personal Information
      </h3>

      <TextInput name="location.addressLine1" placeholder="Address Line 1" />

      <TextInput
        name="location.addressLine2"
        placeholder="Address Line 2 (Optional)"
      />

      <div className="flex gap-5">
        <UserLocationForm
          formModeOff={true}
          hideLabels
          stateName="location.state"
          cityName="location.city"
        />
      </div>

      <TextInput name="location.zip" placeholder="Postal Code" />

      {isGoogleSignupCompleted && (
        <div>
          <div className="flex gap-5">
            {isDev && (
              <div className="w-[120px]">
                <SelectInput
                  name="countryCode"
                  onChange={(value) => {
                    const title = getTitleFromCode(value);
                    setFieldValue('countrySymbol', title);
                  }}>
                  {countryCodes.map((item) => (
                    <SelectOption
                      key={item.code}
                      value={item.code}
                      child={item.title}
                    />
                  ))}
                </SelectInput>
              </div>
            )}

            <TextInput type="tel" name="phone" placeholder="Phone" />
          </div>

          <TextInput
            name="referralCode"
            placeholder="Referral Code (optional)"
            className="mt-4"
          />

          <p className="text-sm text-slate-500">
            By entering my mobile number, I agree to receive important account
            related texts from Rapid Rentals such as rental confirmations and
            reminders. Message frequency varies by account usage. Message & data
            rates may apply. Reply STOP to opt out.
          </p>
        </div>
      )}

      <p className="text-center text-[#848484] text-[12px] mt-5">
        Initial launch is limited to Minnesota. We're excited to expand to
        additional markets in the near future. Please follow for updates.
      </p>

      <div className="flex flex-col gap-3 mt-5">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="w-full h-auto">
          Back
        </Button>

        <Button
          type="submit"
          variant="solid"
          isLoading={isLoading}
          colorScheme="primary"
          className="btn-var-solid w-full h-auto btn-var-solid">
          Create Account
        </Button>
      </div>
    </div>
  );
};

export default AddressForm;
